import { useState, type FormEvent } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { IconArrowLeft, IconLoader2 } from "@tabler/icons-react";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Logo } from "@/components/ui/logo";
import { cn } from "@/lib/utils/cn";
import { apiErrorMessage } from "@/lib/http";
import { activateAccount } from "../api";
import { PasswordFields } from "../components/PasswordFields";

/**
 * Ativação de conta convidada pelo backoffice: o token vem do link do e-mail
 * (/activate-account?token=). Define a senha → conta nasce pendente (modelo
 * híbrido) → tela de acompanhamento.
 */
export function ActivateAccountPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") ?? "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!token) {
      toast.error("Link de ativação inválido.");
      return;
    }
    if (password.length < 8) {
      toast.error("A senha deve ter ao menos 8 caracteres.");
      return;
    }
    if (password !== confirm) {
      toast.error("As senhas não coincidem.");
      return;
    }

    setSubmitting(true);
    try {
      const result = await activateAccount({ token, password });
      toast.success("Conta criada! Acompanhe a análise abaixo.");
      navigate("/pending", { replace: true, state: result });
    } catch (error) {
      toast.error(apiErrorMessage(error, "Link inválido ou expirado."));
      setSubmitting(false);
    }
  }

  return (
    <Card className="p-8 shadow-card border-border/60">
      <div className="mb-6 flex flex-col items-center gap-3 text-center">
        <Logo />
        <div>
          <h1 className="text-lg font-bold tracking-tight text-ink">Ativar conta</h1>
          <p className="mt-1 text-sm text-ink-muted">
            Defina uma senha para concluir o cadastro da sua imobiliária.
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <PasswordFields
          password={password}
          confirm={confirm}
          onPasswordChange={setPassword}
          onConfirmChange={setConfirm}
        />

        <button
          type="submit"
          disabled={submitting}
          className={cn(
            "flex w-full items-center justify-center gap-2 rounded-xl bg-brand-grad py-3.5 text-sm font-bold text-white shadow-glow transition-all hover:scale-[1.01] active:scale-[0.99] disabled:opacity-70",
            submitting && "cursor-not-allowed",
          )}
        >
          {submitting ? <IconLoader2 className="animate-spin" size={18} /> : "Ativar conta"}
        </button>
      </form>

      <Link
        to="/login"
        className="mt-6 inline-flex w-full items-center justify-center gap-1.5 text-sm font-medium text-ink-muted hover:text-ink"
      >
        <IconArrowLeft size={15} aria-hidden />
        Voltar para o login
      </Link>
    </Card>
  );
}
